import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * OnboardingTour — first-login walkthrough.
 *
 * Renders a step-by-step coachmark over key targets across the homepage:
 *   1. AI Coach (header link)
 *   2. Food / Tracker
 *   3. Phoenix Nest
 *   4. Shop
 *   5. Daily Egg Spin Wheel (Homepage card)
 *   6. Profile
 *
 * State persistence: localStorage key `omni:tour-done:v1`. The Profile page
 * exposes a "Re-take tour" button which clears that key + dispatches the
 * window event `omnimuscle:start-tour` consumed here.
 *
 * Targets are looked up by `data-testid`. A step is silently skipped if its
 * target isn't found (defensive — the tour never blocks the user).
 */
const TOUR_STORAGE_KEY = "omni:tour-done:v1";

const STEPS = [
  {
    id: "welcome",
    target: null, // centered modal
    title: "👋 Welcome to OmniMuscle",
    body:
      "60 seconds to learn the ropes — eggs, tokens, the AI Coach, and the daily Spin Wheel. You can re-take this tour anytime from your Profile.",
    cta: "Let's go →",
  },
  {
    id: "nav-ai",
    target: "[data-testid=nav-ai-coach]",
    title: "🤖 AI Coach",
    body:
      "Generate personalized workout + nutrition plans. Free users get a taste; Premium unlocks unlimited plans.",
  },
  {
    id: "nav-food",
    target: "[data-testid=nav-food]",
    title: "🍳 Food Tracker",
    body:
      "Log meals manually, scan barcodes, or snap a photo — the AI auto-fills macros. Streaks earn 🐦‍🔥 Phoenix Tokens.",
  },
  {
    id: "nav-nest",
    target: "[data-testid=nav-nest]",
    title: "🪹 Phoenix Nest",
    body:
      "Every activity drops a 🥚 Egg. Wait for it to incubate, hatch it, and earn Nests + bonus items. Rare eggs roll up the 8-tier system.",
  },
  {
    id: "nav-shop",
    target: "[data-testid=nav-shop]",
    title: "🛍️ Phoenix Shop",
    body:
      "Spend Omni Dollars on real merch + daily-rotation eggs. Even Chief Cook David Lauer's signature egg shows up here sometimes 👨‍🍳.",
  },
  {
    id: "spin-card",
    target: "[data-testid=egg-spin-card], [data-testid=egg-spin-go-btn]",
    title: "🎰 Daily Egg Spin",
    body:
      "One free spin per Pacific Time day. 6-sided wheel that lands on 1-5 eggs (or 'Spin Again'). Pure gold every morning.",
  },
  {
    id: "profile",
    target: "[data-testid=nav-profile]",
    title: "👤 Your Profile",
    body:
      "Manage friends, settings, social handles, privacy toggles — and re-take this tour anytime from here.",
    cta: "Got it",
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const [stepIndex, setStepIndex] = useState(-1); // -1 = inactive
  const [targetRect, setTargetRect] = useState(null);
  const observerRef = useRef(null);

  // Hide the tour entirely when the player is in the opening cinematic
  // OR on any action/combat page (battle, arena, coliseum, wilds) — these
  // pages need every click for gameplay and the tour overlay was hijacking
  // skill clicks (see /app/test_reports/iteration_68.json).
  const NO_TOUR_ROUTES = ["/intro", "/battle", "/arena", "/coliseum", "/wilds", "/awaken-at-tomb", "/pick-class", "/guild/hall", "/guild/skills"];
  const pathname = typeof window !== "undefined" ? (window.location.pathname || "") : "";
  const onSuppressedRoute = NO_TOUR_ROUTES.some((p) => pathname.startsWith(p));
  const onIntroRoute = onSuppressedRoute;

  const start = useCallback(() => {
    setStepIndex(0);
  }, []);

  const finish = useCallback(() => {
    try { localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch { /* noop */ }
    setStepIndex(-1);
  }, []);

  // Auto-start on first authed visit — but ONLY for fresh accounts.
  // Feb 2026: previously the tour re-fired whenever localStorage cleared,
  // making returning users (like the Owner) see a dim page and mistake it
  // for a "blank profile". We now skip auto-start if the user's account is
  // older than 3 days — they've already used the app and don't need it.
  useEffect(() => {
    if (!user) return;
    if (onSuppressedRoute) return;
    let done = "0";
    try { done = localStorage.getItem(TOUR_STORAGE_KEY) || "0"; } catch { /* noop */ }
    if (done === "1") return;

    // Skip auto-start for anyone whose account is > 3 days old.
    // The "Re-take Tour" button on Profile still lets them opt back in.
    try {
      const createdAt = user?.created_at ? new Date(user.created_at).getTime() : null;
      if (createdAt && (Date.now() - createdAt) > 3 * 24 * 60 * 60 * 1000) {
        localStorage.setItem(TOUR_STORAGE_KEY, "1");
        return;
      }
    } catch { /* noop */ }

    const t = setTimeout(() => {
      const cur = (typeof window !== "undefined" ? window.location.pathname : "") || "";
      if (NO_TOUR_ROUTES.some((p) => cur.startsWith(p))) return;
      setStepIndex(0);
    }, 1500);
    return () => clearTimeout(t);
  }, [user, onSuppressedRoute]);

  // External trigger from Profile "Re-take tour"
  useEffect(() => {
    const handler = () => {
      try { localStorage.removeItem(TOUR_STORAGE_KEY); } catch { /* noop */ }
      start();
    };
    window.addEventListener("omnimuscle:start-tour", handler);
    return () => window.removeEventListener("omnimuscle:start-tour", handler);
  }, [start]);

  // Measure the active step's target rect
  useEffect(() => {
    if (stepIndex < 0) {
      setTargetRect(null);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }
    const step = STEPS[stepIndex];
    if (!step.target) {
      setTargetRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setTargetRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
      // Scroll target into view
      const above = r.top < 80;
      const below = r.bottom > window.innerHeight - 40;
      if (above || below) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
  }, [stepIndex]);

  if (stepIndex < 0) return null;
  if (onIntroRoute) return null;   // hide entirely during the cinematic
  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  // Position the coachmark popover near the target. Centered if no target.
  let popStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };
  if (targetRect) {
    const popWidth = 320;
    const margin = 16;
    // Default: place BELOW the target if there's room, else ABOVE
    const targetBottom = targetRect.top - window.scrollY + targetRect.height;
    const targetTop = targetRect.top - window.scrollY;
    let top, transform;
    if (window.innerHeight - targetBottom > 220) {
      top = targetBottom + margin;
      transform = "none";
    } else {
      top = targetTop - margin;
      transform = "translateY(-100%)";
    }
    // Center horizontally on the target, clamp into the viewport
    let left = targetRect.left - window.scrollX + targetRect.width / 2 - popWidth / 2;
    left = Math.max(12, Math.min(window.innerWidth - popWidth - 12, left));
    popStyle = { position: "fixed", top, left, transform, width: popWidth };
  }

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[120] pointer-events-none"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop — click anywhere OUTSIDE the popover to skip the tour.
          Feb 2026: opacity dropped from /70 → /40 and click now dismisses
          the tour so users don't perceive the page as "completely black". */}
      <div
        className="absolute inset-0 bg-black/40 pointer-events-auto cursor-pointer"
        onClick={finish}
        data-testid="onboarding-tour-backdrop"
      />
      {/* 🅧 Big floating close button — always in the corner, always reachable */}
      <button
        type="button"
        onClick={finish}
        data-testid="onboarding-tour-close"
        aria-label="Close tour"
        className="fixed top-3 right-3 z-[125] w-11 h-11 rounded-full bg-black/80 hover:bg-red-900 text-white text-xl font-black flex items-center justify-center border-2 border-[#D4AF37] pointer-events-auto shadow-2xl transition-colors"
      >
        ×
      </button>
      {targetRect && (
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: targetRect.top - window.scrollY - 8,
            left: targetRect.left - window.scrollX - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.35), 0 0 24px rgba(212, 175, 55, 0.55)",
            border: "2px solid #D4AF37",
            borderRadius: 8,
            transition: "all 0.25s ease",
          }}
        />
      )}
      {/* Popover */}
      <div
        data-testid="onboarding-tour-popover"
        className="pointer-events-auto bg-[#0F0F0F] border-2 border-[#D4AF37] p-5 shadow-2xl"
        style={popStyle}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Tour · {stepIndex + 1} / {STEPS.length}
          </span>
          <button
            data-testid="onboarding-tour-skip"
            onClick={finish}
            className="text-neutral-500 hover:text-white text-xs uppercase tracking-widest"
          >
            Skip ×
          </button>
        </div>
        <div className="font-display text-xl text-white leading-tight mb-2">
          {step.title}
        </div>
        <div className="text-sm text-neutral-300 leading-relaxed">
          {step.body}
        </div>
        <div className="flex items-center justify-between gap-2 mt-4">
          <button
            data-testid="onboarding-tour-back"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={isFirst}
            className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← Back
          </button>
          <button
            data-testid="onboarding-tour-next"
            onClick={() => (isLast ? finish() : setStepIndex((i) => i + 1))}
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-2.5 px-4 transition-colors"
          >
            {isLast ? (step.cta || "Finish") : (step.cta || "Next →")}
          </button>
        </div>
      </div>
    </div>
  );
}
